import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getBasePathByUserRole } from "../../../utils/roleRoutes";
import AdminAppBar from "../../adminComponents/AdminAppBar";
import { ToastContainer, toast } from "react-toastify";
import { FaFacebook } from "react-icons/fa6";
import CustomHeading from "../../common/CustomHeading";
import { CgAddR } from "react-icons/cg";
import { FacebookSDK, fbLogin } from "../../../utils/FacebookSDK";
import axios from "axios";
import AccountDropdown from "./AccountDropdown";
import LeadSourceDropdown from "../../Form/LeadTable/LeadSourceDropdown";
import CustomModel from "../../common/CustomModel";
import { baseUrl } from '../../../config/clientsConfig'

const IntegrationModule = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("admin"); // Default to admin tab
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [userAccounts, setUserAccounts] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Approval modal states
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [selectedLeadSource, setSelectedLeadSource] = useState(null);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      try {
        // Get current user role from localStorage
        const userData = JSON.parse(localStorage.getItem("userData"));
        const userRole = userData?.user?.role;
        
        // Fetch accounts data based on role
        const response = await axios.get(`${baseUrl}/accounts?isAdminVerified=true`);
        const accountsData = response.data;
        
        // Separate data based on account type or status
        const connectedAccountsData = accountsData.filter(account => 
          account.role === "Admin"
        );
        
        const userAccountsData = accountsData.filter(account => 
          account.isAdminVerified && account.role != "Admin"
        );
        
         // Fetch accounts data based on role
        const pendingResponse = await axios.get(`${baseUrl}/accounts?isAdminVerified=false`);
        const pendingAccountsData = pendingResponse.data;

        const pendingRequestsData = pendingAccountsData.filter(account => 
          !account.isAdminVerified && account.role != "Admin"
        );
        
        setConnectedAccounts(connectedAccountsData);
        setUserAccounts(userAccountsData);
        setPendingRequests(pendingRequestsData);
        
        // Debug: Log the data structure to see property names
        if (connectedAccountsData.length > 0) {
          console.log("Connected Account Structure:", Object.keys(connectedAccountsData[0]));
        }
        if (userAccountsData.length > 0) {
          console.log("User Account Structure:", Object.keys(userAccountsData[0]));
        }
        
      } catch (error) {
        console.error("Error fetching accounts data:", error);
        // Set empty arrays in case of error
        setConnectedAccounts([]);
        setUserAccounts([]);
        setPendingRequests([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close dropdown if modal is open
      if (showApprovalModal) {
        return;
      }
      
      if (activeDropdown !== null) {
        // Check if click is inside dropdown area
        const dropdownElement = event.target.closest('.relative');
        const isDropdownButton = event.target.closest('button');
        
        // Don't close if clicking inside dropdown or its button
        if (!dropdownElement && !isDropdownButton) {
          setActiveDropdown(null);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [activeDropdown, showApprovalModal]);

  // Define options for different tabs
  const adminAccountOptions = [
    { key: 'reconnect', label: 'Reconnect', destructive: false },
    { key: 'disconnect', label: 'Disconnect', destructive: false },
    { key: 'delete', label: 'Delete', destructive: true }
  ];

  const userAccountOptions = [
    { key: 'disconnect', label: 'Disconnect', destructive: false },
    { key: 'delete', label: 'Delete', destructive: true }
  ];

  const pendingRequestOptions = [
    { key: 'approve', label: 'Approve', primary: true },
    { key: 'reject', label: 'Reject', destructive: true }
  ];

  const toggleDropdown = (accountId) => {
    setActiveDropdown(activeDropdown === accountId ? null : accountId);
  };

  const handleReconnect = async (accountId) => {
    try {
      // Show loading toast
      const toastId = toast.loading("Reconnecting Facebook account...");

      // 1. Initialize Facebook SDK
      await FacebookSDK();

      // 2. Trigger Facebook Login with code flow
      const response = await fbLogin();

      // Get current user's UID and role from localStorage
      const userData = JSON.parse(localStorage.getItem("userData"));
      const currentUserUid = userData?.user?.uid;
      const userRole = userData?.user?.role;

      // 3. Make API call to reconnect the account (using the same endpoint)
      await axios.post(`${baseUrl}/accounts`, {
        "accessToken": response.authResponse.accessToken,
        "uid": currentUserUid,
        "isAdminRequest": userRole === "Admin",
        "role": userRole,
        "reconnectAccountId": accountId  // Add this to indicate it's a reconnection
      });

      // Update toast to success
      toast.update(toastId, {
        render: "Facebook account reconnected successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: true,
      });

      // Update the local state to reflect the reconnection
      setConnectedAccounts(prev => 
        prev.map(account => 
          account.accountId === accountId 
            ? { ...account, isConnected: true }
            : account
        )
      );
      
      setUserAccounts(prev => 
        prev.map(account => 
          account.accountId === accountId 
            ? { ...account, status: 'Active' }
            : account
        )
      );

      // Optionally refresh the data after reconnection
      // window.location.reload(); // Uncomment if you want to refresh the entire page

    } catch (error) {
      console.error("❌ Error during Facebook reconnection:", error);
      
      // Show error toast
      toast.error("Failed to reconnect Facebook account. Please try again.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setActiveDropdown(null);
    }
  };

  const handleDisconnect = async (accountId) => {
    try {
      // Get current user's UID from localStorage
      const userData = JSON.parse(localStorage.getItem("userData"));
      const currentUserUid = userData?.user?.uid;

      // Show loading toast
      const toastId = toast.loading("Disconnecting Facebook account...");

      // Make API call to disconnect the account
      await axios.delete(`${baseUrl}/accounts/${accountId}`);

      // Update toast to success
      toast.update(toastId, {
        render: "Facebook account disconnected successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: true,
      });

      // Update the local state to reflect the disconnection
      setConnectedAccounts(prev => 
        prev.map(account => 
          account.accountId === accountId 
            ? { ...account, isConnected: false }
            : account
        )
      );
      
      setUserAccounts(prev => 
        prev.map(account => 
          account.accountId === accountId 
            ? { ...account, status: 'Disconnected' }
            : account
        )
      );

    } catch (error) {
      console.error("Error disconnecting Facebook account:", error);
      
      // Show error toast
      toast.error("Failed to disconnect Facebook account. Please try again.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setActiveDropdown(null);
    }
  };

  const handleDelete = (accountId) => {
    // Add deletion logic here
    setActiveDropdown(null);
  };

  const handleApprove = useCallback((requestId) => {
    setSelectedRequestId(requestId);
    setActiveDropdown(null);
    
    // Use setTimeout to prevent immediate modal closure due to event bubbling
    setTimeout(() => {
      setShowApprovalModal(true);
    }, 50);
  }, []);

  const handleReject = (requestId) => {
    // Add rejection logic here
    setActiveDropdown(null);
  };

  // Handle dropdown option clicks
  const handleDropdownOptionClick = (action, accountId) => {
    switch (action) {
      case 'reconnect':
        handleReconnect(accountId);
        break;
      case 'disconnect':
        handleDisconnect(accountId);
        break;
      case 'delete':
        handleDelete(accountId);
        break;
      case 'approve':
        handleApprove(accountId);
        break;
      case 'reject':
        handleReject(accountId);
        break;
      default:
        break;
    }
  };

  // Navigate to ConnectedPage when admin account card is clicked
  const handleAccountCardClick = (account) => {
    const path = getBasePathByUserRole();
    navigate(`${path}/web-hooks-connected-page`, {
      state: {
        selectedMedia: "Facebook",
        accountData: account
      }
    });
  };

  const handleConnection = async (select) => {
    try {
      // 1. Initialize Facebook SDK
      await FacebookSDK();

      // 2. Trigger Facebook Login with code flow
      const response = await fbLogin();

      // 3. Extract code from response


      const code = response.authResponse.code;

      // Get current user's UID and role from localStorage
      const userData = JSON.parse(localStorage.getItem("userData"));
      const currentUserUid = userData?.user?.uid;
      const userRole = userData?.user?.role;

      await axios.post(`${baseUrl}/accounts`, {
        "accessToken": response.authResponse.accessToken,
        "uid": currentUserUid,
        "isAdminRequest": userRole === "Admin",
        "role": userRole
      });

      // if (!code) {
      //   throw new Error("No authorization code returned from Facebook.");
      // }

      // const tokenUrl = "https://graph.facebook.com/v23.0/oauth/access_token";

      // const getAccessToken = await axios.get(tokenUrl, {
      //   params: {
      //     client_id: process.env.REACT_APP_FACEBOOK_CLIENT_ID || "1863257657641596",
      //     client_secret: process.env.REACT_APP_FACEBOOK_CLIENT_SECRET || "c3f369ddd1f5855043f4863dd160150e", 
      //     redirect_uri: process.env.REACT_APP_FACEBOOK_REDIRECT_URI || "https://crm.glfunnel.com/auth/callback", 
      //     code: code,
      //   },
      // });

    } catch (error) {
      console.error("❌ Error during Facebook login:", error);
    }
  };

  const handleApprovalSubmit = () => {
    if (selectedLeadSource && selectedRequestId) {
      // Show success toast
      toast.success("Integration approved successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Navigate to connected page with selected lead source
      const path = getBasePathByUserRole();
      navigate(`${path}/web-hooks-connected-page`, {
        state: {
          selectedMedia: 'Facebook',
          defaultLeadSource: selectedLeadSource,
          approvedRequestId: selectedRequestId
        },
      });
      
      // Close modal and reset states
      setShowApprovalModal(false);
      setSelectedRequestId(null);
      setSelectedLeadSource(null);
    }
  };

  const AdminTab = () => (
    <div className="px-6 flex gap-6 mt-8 flex-wrap">
      {loading ? (
        <div className="flex justify-center items-center py-20 w-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-customGreen"></div>
          <span className="ml-3 text-gray-500">Loading...</span>
        </div>
      ) : (
        <>
          {/* Connected Accounts */}
          {connectedAccounts.map((account) => (
            <div
              key={account.accountId}
              onClick={() => handleAccountCardClick(account)}
              className="w-[400px] h-[120px] cursor-pointer bg-white p-4 rounded-lg shadow-sm hover:shadow-md transform hover:scale-[1.01] transition-all duration-200 ease-in-out relative"
            >
              <div className="flex items-center justify-between h-full">
                <div className="flex items-center gap-3 flex-1">
                  <FaFacebook className="text-5xl text-blue-500" />
                  <div className="flex flex-col gap-1">
                    <div className="font-semibold text-base text-gray-900">
                      Account : {account.accountName}
                    </div>
                    <div className="text-sm text-gray-500">
                      No of Page : {account.totalPageCount || 0}
                    </div>
                  </div>
                </div>
                <div 
                  className="flex flex-col gap-3 items-end justify-between h-full py-1"
                  onClick={(e) => e.stopPropagation()} // Prevent card click when clicking dropdown
                >
                  <div className={`text-xs font-medium bg-gray-50 shadow-sm rounded-full py-1 px-3 transition-colors duration-300 ${
                    account.isConnected ? 'text-green-600' : 'text-orange-500'
                  }`}>
                    {account.isConnected ? 'Connected' : 'Not Connected'}
                  </div>
                  <AccountDropdown
                    accountId={account.accountId}
                    activeDropdown={activeDropdown}
                    toggleDropdown={toggleDropdown}
                    options={adminAccountOptions}
                    onOptionClick={handleDropdownOptionClick}
                  />
                </div>
              </div>
            </div>
          ))}

          {/* Add Another Account Card - Only show if no accounts exist */}
          {connectedAccounts.length === 0 && (
            <div 
              onClick={handleConnection} 
              className="w-[400px] h-[120px] border-dashed border-2 border-gray-300 cursor-pointer p-4 rounded-lg hover:border-gray-400 transform hover:scale-[1.01] transition-all duration-200 ease-in-out"
            >
              <div className="flex flex-col justify-center text-center items-center gap-2 h-full">
                <CgAddR className="text-3xl text-gray-400" />
                <div className="text-sm text-gray-600 font-medium">Add Another Account</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const UserTab = () => (
    <div className="px-6 mt-8 flex gap-6 flex-wrap">
      {loading ? (
        <div className="flex justify-center items-center py-20 w-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-customGreen"></div>
          <span className="ml-3 text-gray-500">Loading...</span>
        </div>
      ) : userAccounts.length > 0 ? (
        userAccounts.map((account) => (
          <div
            key={account.accountId || account.id}
            className="w-[400px] h-[120px] cursor-pointer bg-white p-4 rounded-lg shadow-sm hover:shadow-md transform hover:scale-[1.01] transition-all duration-200 ease-in-out"
          >
            <div className="flex items-center justify-between h-full">
              <div className="flex items-center gap-3">
                <FaFacebook className="text-5xl text-blue-500" />
                <div className="flex flex-col gap-1">
                  <div className="font-semibold text-base text-gray-900">
                    Account : {account.accountName}
                  </div>
                  <div className="text-sm text-gray-500">
                    User : {account.userName}
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-3 items-end justify-between h-full py-1">
                <div className={`text-xs font-medium bg-gray-50 shadow-sm rounded-full py-1 px-3 transition-colors duration-300 ${
                  account.status === 'Active' ? 'text-green-600' : 'text-orange-500'
                }`}>
                  {account.status}
                </div>
                <AccountDropdown
                  accountId={account.accountId || account.id}
                  activeDropdown={activeDropdown}
                  toggleDropdown={toggleDropdown}
                  options={userAccountOptions}
                  onOptionClick={handleDropdownOptionClick}
                />
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="w-full text-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="text-gray-400 text-5xl">👥</div>
            <p className="text-gray-600 text-base font-medium">No user accounts found</p>
            <p className="text-gray-400 text-sm">User accounts will appear here when available</p>
          </div>
        </div>
      )}
    </div>
  );

  const PendingTab = () => (
    <div className="px-6 mt-8 flex gap-6 flex-wrap">
      {loading ? (
        <div className="flex justify-center items-center py-20 w-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-customGreen"></div>
          <span className="ml-3 text-gray-500">Loading...</span>
        </div>
      ) : pendingRequests.length > 0 ? (
        pendingRequests.map((request) => (
          <div
            key={request.accountId || request.id}
            className="w-[400px] h-[120px] cursor-pointer bg-white p-4 rounded-lg shadow-sm hover:shadow-md transform hover:scale-[1.01] transition-all duration-200 ease-in-out"
          >
            <div className="flex items-center justify-between h-full">
              <div className="flex items-center gap-3">
                <FaFacebook className="text-5xl text-blue-500" />
                <div className="flex flex-col gap-1">
                  <div className="font-semibold text-base text-gray-900">
                    Request : {request.requestName}
                  </div>
                  <div className="text-sm text-gray-500">
                    Requested by : {request.requestedBy}
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-3 items-end justify-between h-full py-1">
                <div className="text-xs font-medium bg-orange-50 text-orange-600 rounded-full py-1 px-3 transition-colors duration-300">
                  Pending
                </div>
                <AccountDropdown
                  accountId={request.accountId || request.id}
                  activeDropdown={activeDropdown}
                  toggleDropdown={toggleDropdown}
                  options={pendingRequestOptions}
                  onOptionClick={handleDropdownOptionClick}
                />
              </div>
            </div>
          </div>
        ))
      ) : (
        <div className="w-full text-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="text-gray-400 text-5xl">⏳</div>
            <p className="text-gray-600 text-base font-medium">No pending requests</p>
            <p className="text-gray-400 text-sm">Pending integration requests will appear here</p>
          </div>
        </div>
      )}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case "admin":
        return <AdminTab />;
      case "user":
        return <UserTab />;
      case "pending":
        return <PendingTab />;
      default:
        return null;
    }
  };

  return (
    <div className="pt-14 bg-appGrayBackground min-h-screen">
      <AdminAppBar />
      <ToastContainer />

      {/* Heading and Tabs */}
      <div className="mt-8 px-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <CustomHeading title="Facebook Integration" isIcon={true} />
          </div>
          {/* Tab Navigation */}
          <div className="flex items-center gap-0 border-b border-gray-200">
            {["admin", "user", "pending"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 text-base font-medium border-b-2 transition-colors duration-200 ${
                  activeTab === tab
                    ? "text-customGreen border-customGreen"
                    : "text-gray-600 border-transparent hover:text-gray-800"
                }`}
              >
                {tab === "admin"
                  ? "Admin Account"
                  : tab === "user"
                  ? "Users Account"
                  : "Pending Request"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Approval Modal */}
      <CustomModel
        isOpen={showApprovalModal}
        isClose={() => {
          setShowApprovalModal(false);
          setSelectedRequestId(null);
          setSelectedLeadSource(null);
        }}
        title="Select Lead Source"
        content={
          <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Choose Lead Source for this Integration
              </label>
              <LeadSourceDropdown
                onSelect={(selectedItem) => {
                  setSelectedLeadSource(selectedItem);
                }}
                placeholder="Select Lead Source"
              />
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedRequestId(null);
                  setSelectedLeadSource(null);
                }}
                className="px-4 py-2 rounded-md border bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleApprovalSubmit}
                disabled={!selectedLeadSource}
                className={`px-4 py-2 rounded-md text-white transition ${
                  selectedLeadSource
                    ? "bg-customGreen hover:bg-green-600"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                Approve & Continue
              </button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default IntegrationModule;
